// /admin homepage. Greeting strip → 4 KPI cards → "Manage" tile grid.
// No AdminPageHeader / no "Admin Panel" title — context is signalled
// by the persistent SideNav. Greeting reuses the existing shared helper
// so the wording matches what the header shows for the same session.
import {
    ListOrdered, Warehouse, ArrowDownUp, Users, Monitor, HardDrive, Settings,
    PackageOpen, PackageCheck, UserCheck, Boxes, Hand, Truck, Gauge, Undo2,
} from "lucide-react";
import { useTranslation } from "../i18n";
import { getGreeting } from "../components/helpers/greeting";
import KpiCard from "../components/admin/KpiCard";
import AdminTile from "../components/admin/AdminTile";
import { useAccess } from "../access/AccessContext";
import AdminWarehouseSwitcher from "../components/admin/AdminWarehouseSwitcher";
import { useAdminDashboard, KpiState } from "../hooks/useAdminDashboard";
import { OrderStageCounts, SortingCounts } from "../api/adminDashboard";
import { usePageTitle } from "../hooks/usePageTitle";
import "../styles/admin-home.css";

const todayLong = (lang: "en" | "pl") => {
    try {
        return new Date().toLocaleDateString(lang === "pl" ? "pl-PL" : "en-GB", {
            weekday: "long", day: "numeric", month: "short",
        });
    } catch { return ""; }
};

const Admin = () => {
    const { t, language } = useTranslation();
    // Tiles are hidden when the access context says the key is not granted; the route
    // guard and the server remain the control. Until the context is ready (or if it
    // failed) every tile shows, exactly as today.
    const { hasAccess, ready: accessReady, failed: accessFailed } = useAccess();
    const show = (key: string) => !(accessReady && !accessFailed) || hasAccess(key);
    usePageTitle("admin");

    // Reads session lazily so the page stays a render away from a fresh
    // sessionStorage value after a re-login. No memo here — getGreeting
    // is itself session-stable via the seed it stores.
    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null
        ? JSON.parse(userData)
        : { name: "", warehouse: "" };
    const firstName = userObject.name ? userObject.name.split(" ")[0] : "";
    const greeting = firstName ? getGreeting(firstName, language) : "";

    const dateLabel = todayLong(language);
    const warehouseLabel = userObject.warehouse || "";

    const {
        unfulfilled, inPicking, inSorting, awaitingDispatch,
        fulfilledToday, activeToday, totesToPutAway, fulfilmentMode,
    } = useAdminDashboard();

    // Newport and Poland do not pick: their orders go through Sorter 1 and
    // Sorter 2 (zone storage), so the picking card is noise there and the
    // sorting card reads the sorters instead of orders.status (per Jack,
    // 2026-10-20). The mode comes from the BE, not a warehouse list.
    const usesSorters = fulfilmentMode === "sorters";
    const sortersLine = (kpi: KpiState<SortingCounts>) => {
        const s = kpi.value?.sorters;
        if (!s) return null;
        return {
            text: t("kpi_sorters_breakdown", {
                s1: s.sorter1.items.toLocaleString(),
                s2: s.sorter2.items.toLocaleString(),
            }),
        };
    };
    // The sorters' queue lives on the Zone Storage page, which needs its own
    // key; without it the card is a static tile rather than a dead link.
    const sortingTo = usesSorters
        ? (show("admin.zone_storage") ? "/admin/zone-storage" : undefined)
        : "/admin/warehouse/orders?status=sorting&date=all";

    // Order-stage cards lead with item units (the truer workload signal
    // — one order can be 1 or 40 items) and carry the order count as
    // the sub-line, matching the row count on the click-through queue.
    const stageValue = (kpi: KpiState<OrderStageCounts>) =>
        kpi.value ? kpi.value.items.toLocaleString() : null;
    const stageSub = (kpi: KpiState<OrderStageCounts>) =>
        kpi.value ? { text: t("kpi_orders_sub", { count: kpi.value.orders.toLocaleString() }) } : null;
    // Unfulfilled only: how much of the headline is stock blocked (per
    // Alex, 2026-08-25). The headline still counts those units — this is
    // the breakdown, orange when there's anything blocked. Omitted when
    // the BE couldn't run the gate (no sub-count in the payload).
    const stockBlockedLine = (kpi: KpiState<OrderStageCounts>) => {
        const sb = kpi.value?.stockBlocked;
        if (!sb) return null;
        return {
            text: t("kpi_stock_blocked_sub", { units: sb.items.toLocaleString() }),
            tone: sb.items > 0 ? "accent" as const : "neutral" as const,
        };
    };

    return (
        <div className="admin-home">
            <p className="admin-home-greeting">
                {greeting && <span className="admin-home-greeting-name">{greeting}</span>}
                {(dateLabel || warehouseLabel) && (
                    <span className="admin-home-greeting-meta">
                        {greeting && " — "}
                        {dateLabel}
                        {dateLabel && warehouseLabel && " · "}
                    </span>
                )}
                <AdminWarehouseSwitcher />
            </p>

            <div className={`admin-home-kpis${usesSorters ? " admin-home-kpis--six" : ""}`}>
                <KpiCard
                    labelKey="kpi_unfulfilled"
                    state={unfulfilled.state}
                    value={stageValue(unfulfilled)}
                    subLine={stageSub(unfulfilled)}
                    extraLine={stockBlockedLine(unfulfilled)}
                    onRetry={unfulfilled.retry}
                    to="/admin/warehouse/orders?status=unfulfilled&date=all"
                    icon={<PackageOpen size={16} strokeWidth={1.6} />}
                    tone="amber"
                />
                {!usesSorters && (
                    <KpiCard
                        labelKey="kpi_in_picking"
                        state={inPicking.state}
                        value={stageValue(inPicking)}
                        subLine={stageSub(inPicking)}
                        onRetry={inPicking.retry}
                        to="/admin/warehouse/orders?status=picking&date=all"
                        icon={<Hand size={16} strokeWidth={1.6} />}
                        tone="orange"
                    />
                )}
                <KpiCard
                    labelKey="kpi_in_sorting"
                    state={inSorting.state}
                    value={stageValue(inSorting)}
                    subLine={stageSub(inSorting)}
                    extraLine={sortersLine(inSorting)}
                    onRetry={inSorting.retry}
                    to={sortingTo}
                    icon={<ArrowDownUp size={16} strokeWidth={1.6} />}
                    tone="teal"
                />
                <KpiCard
                    labelKey="kpi_awaiting_dispatch"
                    state={awaitingDispatch.state}
                    value={stageValue(awaitingDispatch)}
                    subLine={stageSub(awaitingDispatch)}
                    onRetry={awaitingDispatch.retry}
                    to="/admin/warehouse/orders?status=dispatch&date=all"
                    icon={<Truck size={16} strokeWidth={1.6} />}
                    tone="slate"
                />
                <KpiCard
                    labelKey="kpi_fulfilled_today"
                    state={fulfilledToday.state}
                    value={stageValue(fulfilledToday)}
                    subLine={stageSub(fulfilledToday)}
                    onRetry={fulfilledToday.retry}
                    to="/admin/warehouse/orders?status=fulfilled&date=today"
                    icon={<PackageCheck size={16} strokeWidth={1.6} />}
                    tone="green"
                />
                <KpiCard
                    labelKey="kpi_active_today"
                    state={activeToday.state}
                    value={activeToday.value ?? null}
                    onRetry={activeToday.retry}
                    to="/admin/users"
                    icon={<UserCheck size={16} strokeWidth={1.6} />}
                    tone="blue"
                />
                {/* The put-away queue count needs its own key; without it the BE answers 403 and
                    the card would sit on Retry forever (seen with Customer Service, 2026-09-25). */}
                {show("admin.warehouse.putaway_queue") && (
                    <KpiCard
                        labelKey="kpi_totes_to_put_away"
                        state={totesToPutAway.state}
                        value={totesToPutAway.value ?? null}
                        onRetry={totesToPutAway.retry}
                        icon={<Boxes size={16} strokeWidth={1.6} />}
                        tone="purple"
                    />
                )}
            </div>

            <h2 className="admin-home-section-title">{t("manage")}</h2>
            <div className="admin-home-tiles">
                {show("admin.dashboard") && (
                    <AdminTile
                        to="/admin/dashboard"
                        labelKey="dashboard_admin_title"
                        descriptionKey="tile_dashboard_desc"
                        icon={<Gauge size={24} strokeWidth={1.5} />}
                        tone="teal"
                    />
                )}
                {show("admin.orders") && (
                    <AdminTile
                        to="/admin/warehouse/orders"
                        labelKey="orders"
                        descriptionKey="tile_orders_desc"
                        icon={<ListOrdered size={24} strokeWidth={1.5} />}
                        tone="orange"
                    />
                )}
                {show("admin.returns") && (
                    <AdminTile
                        to="/admin/returns"
                        labelKey="rt_title"
                        descriptionKey="tile_returns_desc"
                        icon={<Undo2 size={24} strokeWidth={1.5} />}
                        tone="amber"
                    />
                )}
                {show("admin.warehouse") && (
                    <AdminTile
                        to="/admin/warehouse"
                        labelKey="warehouse_menu_title"
                        descriptionKey="tile_warehouse_desc"
                        icon={<Warehouse size={24} strokeWidth={1.5} />}
                        tone="blue"
                    />
                )}
                {show("admin.zone_storage") && (
                    <AdminTile
                        to="/admin/zone-storage"
                        labelKey="zone_storage"
                        descriptionKey="tile_sorting_desc"
                        icon={<ArrowDownUp size={24} strokeWidth={1.5} />}
                        tone="teal"
                    />
                )}
                {show("admin.users") && (
                    <AdminTile
                        to="/admin/users"
                        labelKey="users"
                        descriptionKey="tile_users_desc"
                        icon={<Users size={24} strokeWidth={1.5} />}
                        tone="purple"
                    />
                )}
                {show("admin.displays") && (
                    <AdminTile
                        to="/admin/displays"
                        labelKey="displays"
                        descriptionKey="tile_displays_desc"
                        icon={<Monitor size={24} strokeWidth={1.5} />}
                        tone="green"
                    />
                )}
                {show("admin.hardware") && (
                    <AdminTile
                        to="/admin/hardware"
                        labelKey="hardware"
                        descriptionKey="tile_hardware_desc"
                        icon={<HardDrive size={24} strokeWidth={1.5} />}
                        tone="amber"
                    />
                )}
                {show("admin.settings") && (
                    <AdminTile
                        to="/admin/settings"
                        labelKey="settings"
                        descriptionKey="tile_settings_desc"
                        icon={<Settings size={24} strokeWidth={1.5} />}
                        tone="slate"
                    />
                )}
            </div>
        </div>
    );
};

export default Admin;
